import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Spacing, Text, MessageBox } from '@reservamos/elements';
import useAdyenApplePay from 'hooks/strategies/useAdyenApplePay';
import usePurchase from 'hooks/store/usePurchase';
import CheckList from 'ui/atoms/CheckList';
import LoadingDots from 'ui/atoms/LoadingDots';
import './AdyenApplePayForm.scss';

/**
 * AdyenApplePayForm component.
 * Render Adyen Apple Pay button and payment instructions.
 * @param {Object} props - onPaymentButtonClick.
 * @param {Function} props.onPaymentButtonClick - Callback when Apple Pay button is clicked.
 * @returns {JSX.Element} Apple Pay payment form with native button.
 */
const AdyenApplePayForm = ({ onPaymentButtonClick }) => {
  const { t } = useTranslation('payment');
  const {
    isUpdating,
    updatingInsurance,
    updatingServiceLevel,
    updatingCarbonOffset,
    applyingDiscount,
    isUpdatingFlatFare,
  } = usePurchase();

  // Initialize Apple Pay hook with callback
  const { containerRef, isLoading, error, isAvailable, isConfirmingPayment } = useAdyenApplePay({
    onPaymentButtonClick,
  });

  // Show confirming state when timeout occurred but payment is still processing
  if (isConfirmingPayment) {
    return (
      <div className="checkout-section applepay-form">
        <div className="applepay-confirming">
          <LoadingDots />
          <Spacing size="XS" />
          <Text>
            {t(
              'apple_pay_status.confirming_payment',
              'Confirming your payment...\nPlease wait while we verify your transaction.',
            )}
          </Text>
        </div>
      </div>
    );
  }

  // Show error if Apple Pay initialization failed (prioritize error over loading)
  if (error && !isLoading) {
    return (
      <div className="checkout-section">
        <MessageBox type="error">
          <Text>
            {t(
              `apple_pay_status.${error}`,
              t('apple_pay_status.default_error', 'Apple Pay is not available at this time'),
            )}
          </Text>
        </MessageBox>
      </div>
    );
  }

  // Determine if we should show loading state
  // Always render the container for the ref to attach, but show loading overlay.
  // Also hide the native Apple Pay button while the purchase total is being recalculated by any
  // ancillary toggle (insurance, service level / "Boleto flexible", carbon offset, flat fare) or
  // a discount code. The native button bypasses the disabled-button guard the standard checkout
  // button already has in Checkout.js, so it must be gated here explicitly.
  const isUpdatingTotal =
    updatingInsurance ||
    updatingServiceLevel ||
    updatingCarbonOffset ||
    applyingDiscount ||
    isUpdatingFlatFare;
  const showLoading = isLoading || isUpdating || isUpdatingTotal || !isAvailable;

  return (
    <div className="checkout-section applepay-form">
      {showLoading ? (
        <div className="applepay-loading">
          <Text>
            {isUpdating
              ? t('apple_pay_status.processing_payment', 'Processing payment...')
              : t('apple_pay_status.initializing', 'Initializing Apple Pay...')}
          </Text>
        </div>
      ) : (
        <Spacing vertical>
          <CheckList
            title={t('label.pay_with_apple_pay', 'Pay with Apple Pay')}
            labels={[
              t('apple_pay_instructions.first', 'Click the Apple Pay button'),
              t(
                'apple_pay_instructions.second',
                'Authorize the payment with Face ID, Touch ID or your passcode',
              ),
              t('apple_pay_instructions.third', 'Your purchase will be processed automatically'),
            ]}
          />
        </Spacing>
      )}

      {/* Always render container so ref can attach - hide visually when loading */}
      <div
        className="applepay-button-container"
        ref={containerRef}
        style={{ display: showLoading ? 'none' : 'block' }}
      />
    </div>
  );
};

AdyenApplePayForm.propTypes = {
  onPaymentButtonClick: PropTypes.func.isRequired,
};

export default AdyenApplePayForm;
